import { INativeRequest } from '../../../typings/IPrebid'
import { Mime } from '../../enums/ContentType'
import { Protocol } from '../../enums/OpenRTB'
import { Device } from '../../helpers'
import Logger from '../../helpers/Logger'
import { NativeDataType } from '../enums/NativeDataType'

const requestCache = {}
/**
 * Consumes the native template's html and builds
 * the native bid request from its html attributes.
 * Caches the result for subsequent bids.
 */
export const buildRequestFromTemplate = (html: string): INativeRequest => {
  if (requestCache[html]) {
    return requestCache[html]
  }

  const request: INativeRequest = {
    sponsoredBy: {
      required: false
    }
  }

  const div = document.createElement('div')
  div.innerHTML = html
  div.querySelectorAll('[data-native-type]').forEach((dataEl: HTMLElement) => {
    const type: NativeDataType = dataEl.getAttribute(
      'data-native-type'
    ) as NativeDataType
    const size = getSizes(dataEl)
    const minSize = getMinSizes(dataEl)
    const required = dataEl.getAttribute('data-native-required') === 'true'
    const len = getLen(dataEl)

    switch (type) {
      case NativeDataType.title:
      case NativeDataType.body:
        request[type] = {
          required,
          len
        }
        break
      case NativeDataType.cta:
      case NativeDataType.clickUrl:
      case NativeDataType.sponsoredBy:
        request[type] = {
          required
        }
        break
      case NativeDataType.icon:
      case NativeDataType.image:
        /**
         * We'll have either an image or a video.
         * Hide both and reveal when they come back.
         */
        dataEl.style.display = 'none'
        request[type] = {
          required,
          size,
          minSize
        }
        break
      case NativeDataType.video:
        dataEl.style.display = 'none'
        request[type] = {
          required,
          mimes: [Mime.javascript, Mime.mp4],
          protocols: [
            Protocol.VAST_2_0,
            Protocol.VAST_3_0,
            Protocol.VAST_2_0_wrapper,
            Protocol.VAST_3_0_wrapper,
            Protocol.VAST_4_0,
            Protocol.VAST_4_0_wrapper
          ]
        }
    }
  })

  requestCache[html] = request

  return request
}

const getSizes = (dataEl: Element): AdSizes => {
  const sizesStr = dataEl.getAttribute('data-native-size')
  const mobileSizesStr = dataEl.getAttribute('data-native-m-size')
  const desktopSizesStr = dataEl.getAttribute('data-native-d-size')
  const tabletSizesStr = dataEl.getAttribute('data-native-t-size')

  try {
    if (Device.isMobile && mobileSizesStr) {
      return JSON.parse(mobileSizesStr)
    } else if (Device.isTablet && tabletSizesStr) {
      return JSON.parse(tabletSizesStr)
    } else if (desktopSizesStr) {
      return JSON.parse(desktopSizesStr)
    } else if (sizesStr) {
      return JSON.parse(sizesStr)
    }
  } catch (e) {
    Logger.debug(e)
  }

  return []
}

const getMinSizes = (dataEl: Element): AdSizes => {
  const sizesStr = dataEl.getAttribute('data-native-min-size')
  const mobileSizesStr = dataEl.getAttribute('data-native-m-min-size')
  const desktopSizesStr = dataEl.getAttribute('data-native-d-min-size')
  const tabletSizesStr = dataEl.getAttribute('data-native-t-min-size')
  try {
    if (Device.isMobile && mobileSizesStr) {
      return JSON.parse(mobileSizesStr)
    } else if (Device.isTablet && tabletSizesStr) {
      return JSON.parse(tabletSizesStr)
    } else if (desktopSizesStr) {
      return JSON.parse(desktopSizesStr)
    } else if (sizesStr) {
      return JSON.parse(sizesStr)
    }
  } catch (e) {
    Logger.debug(e)
  }

  return []
}

const getLen = (dataEl: Element): number => {
  const mobileLen = dataEl.getAttribute('data-native-m-len')
  const tabletLen = dataEl.getAttribute('data-native-t-len')
  const desktopLen = dataEl.getAttribute('data-native-d-len')
  const defaultLen = dataEl.getAttribute('data-native-len') || '50'

  if (Device.isMobile && mobileLen) {
    return parseInt(mobileLen, 10)
  } else if (Device.isTablet && tabletLen) {
    return parseInt(tabletLen, 10)
  } else if (desktopLen) {
    return parseInt(desktopLen, 10)
  }

  return parseInt(defaultLen, 10)
}
